import { EditIcon, TrashIcon } from "@xola/icons";
import { Table, Tooltip } from "@xola/ui-kit";
import { useField } from "formik";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Permission } from "types";
import { IsGranted } from "@/components/Common/IsGranted";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { Language, LanguagesDropdown } from "@/components/Settings/Languages/LanguagesDropdown";
import type { LanguagePreferenceData, PreferenceLanguage } from "@/components/Settings/Languages/LanguageSettings";
import { useDeleteLanguageConfirmationModal } from "@/components/Settings/Languages/useDeleteLanguageConfirmationModal";
import { useCollection } from "@/hooks/useCollection";

export const TranslationLanguages = () => {
    const [{ value: enabledLanguages }, , { setValue }] =
        useField<LanguagePreferenceData["supportedLanguages"]>("supportedLanguages");
    const enabledLanguageLocales = new Set(enabledLanguages.map((language) => language.locale));

    const [languageToBeDeleted, setLanguageToBeDeleted] = useState<PreferenceLanguage | null>(null);
    const { DeleteWindowConfirmationModal, confirmDeleteLanguage } = useDeleteLanguageConfirmationModal();

    const { data: languages } = useCollection<Language, "locale">(["languages"]);
    const addableLanguages = languages?.filter((language) => !enabledLanguageLocales.has(language.locale)) ?? [];

    const handleDelete = async (language: PreferenceLanguage) => {
        setLanguageToBeDeleted(language);

        if (!language.draft) {
            const confirmation = await confirmDeleteLanguage();

            if (!confirmation) {
                // User clicks "Cancel" from the delete language confirmation modal
                return;
            }
        }

        setValue(enabledLanguages.filter((enabledLanguage) => enabledLanguage.locale !== language.locale));
    };

    const handleAdd = (language: Language) => {
        const newLanguage: PreferenceLanguage = {
            locale: language.locale,
            name: language.language,
            draft: true,
        };
        setValue([...enabledLanguages, newLanguage]);
    };

    return (
        <div className="space-y-4">
            {addableLanguages.length > 0 && (
                <div className="flex justify-end">
                    <LanguagesDropdown languages={addableLanguages} onAdd={handleAdd} />
                </div>
            )}

            <LanguagesTable languages={enabledLanguages} onDelete={handleDelete} />

            <DeleteWindowConfirmationModal language={languageToBeDeleted?.name} />
        </div>
    );
};

interface LanguagesTableProps {
    languages: PreferenceLanguage[];
    onDelete: (language: PreferenceLanguage) => Promise<void>;
}

const LanguagesTable = ({ languages, onDelete }: LanguagesTableProps) => {
    const history = useHistory();

    const handleEdit = (language: PreferenceLanguage) => {
        history.push(`/settings/languages/${language.locale}/translations`);
    };

    return (
        <Table>
            <Table.Head>
                <Table.Row>
                    <Table.Header>Language</Table.Header>
                    <Table.Header className="text-right">Actions</Table.Header>
                </Table.Row>
            </Table.Head>
            <Table.Body>
                {languages.length > 0 ? (
                    languages.map((language) => (
                        <Table.Row key={language.locale}>
                            <Table.Cell className="no-translate capitalize">{language.name}</Table.Cell>
                            <Table.Cell className="space-x-2 text-right">
                                <Tooltip
                                    disabled={!language.draft}
                                    content="Please click Save before editing translations for a newly added language"
                                >
                                    <SecondaryButton
                                        disabled={language.draft}
                                        size="tiny"
                                        onClick={() => handleEdit(language)}
                                    >
                                        <EditIcon />
                                    </SecondaryButton>
                                </Tooltip>

                                <IsGranted permissions={Permission.MULTI_LANGUAGE_SUPPORT_ADD_LANGUAGE}>
                                    <SecondaryButton size="tiny" onClick={async () => onDelete(language)}>
                                        <TrashIcon />
                                    </SecondaryButton>
                                </IsGranted>
                            </Table.Cell>
                        </Table.Row>
                    ))
                ) : (
                    <Table.Row>
                        <Table.Cell colSpan={2} className="text-center">
                            No languages added yet
                        </Table.Cell>
                    </Table.Row>
                )}
            </Table.Body>
        </Table>
    );
};
